/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  API_CONFIG,
  API_ENDPOINTS,
  REQUEST_PRIORITY,
  CACHE_KEYS,
  API_ERROR_MESSAGES,
  HTTP_STATUS,
} from "../constants/api";
import {
  type ApiConfig,
  ApiError,
  type QueuedRequest,
  NetworkError,
  AuthenticationError,
  type AuthRequest,
  type AuthResponse,
  type MemberRequest,
  type MemberResponse,
  type Shift,
  type CreateShiftRequest,
  type ShiftStatusRequest,
  type BulkScanRequest,
  type ScanHistoryResponse,
  type BitboxBlockRequest,
  type BitboxBlockResponse,
  type BitboxReportRequest,
  type BitboxReportResponse,
} from "../types/api";
import { TokenManager } from "./api/TokenManager";
import { RequestQueue } from "./api/RequestQueue";
import { CacheManager } from "./api/CacheManager";
import { NetworkMonitor } from "./api/NetworkMonitor";

class ApiService {
  private config: ApiConfig;
  private tokenManager: TokenManager;
  private requestQueue: RequestQueue;
  private cacheManager: CacheManager;
  private networkMonitor: NetworkMonitor;

  constructor(customConfig?: Partial<ApiConfig>) {
    this.config = {
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      retryAttempts: API_CONFIG.RETRY_ATTEMPTS,
      retryDelay: API_CONFIG.RETRY_DELAY,
      enableQueue: true,
      enableCache: true,
      cacheTimeout: API_CONFIG.CACHE_TIMEOUT,
      ...customConfig,
    };

    // Initialize components
    this.tokenManager = new TokenManager(this.config.baseURL);
    this.requestQueue = new RequestQueue(this.config);
    this.cacheManager = new CacheManager(this.config);
    this.networkMonitor = new NetworkMonitor(this.config.baseURL);

    // Set up network event handlers
    this.networkMonitor.onOnline(() => {
      this.requestQueue.processQueue(
        this.networkMonitor.isConnected(),
        this.executeRequest.bind(this)
      );
    });
  }

  // ==================== HTTP REQUEST METHODS ====================

  private buildUrlWithParams(
    url: string,
    params?: Record<string, unknown>,
    bypassCache?: boolean
  ): string {
    const allParams = { ...params };

    // Add cache busting parameter if bypassing cache
    if (bypassCache) {
      allParams._t = Date.now().toString();
    }

    if (!allParams || Object.keys(allParams).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(allParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Handle arrays by adding multiple entries with the same key
        if (Array.isArray(value)) {
          value.forEach((item) => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  private async request<T>(
    url: string,
    options: {
      method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
      data?: Record<string, any>;
      params?: Record<string, any>;
      headers?: Record<string, string>;
      priority?: number;
      bypassAuth?: boolean;
      useCache?: boolean;
      bypassCache?: boolean;
    }
  ): Promise<T> {
    const {
      method,
      data,
      params,
      headers = {},
      priority = REQUEST_PRIORITY.NORMAL,
      bypassAuth = false,
      useCache = false,
      bypassCache = false,
    } = options;

    // Build URL with query parameters and cache busting
    const fullUrl = this.buildUrlWithParams(url, params, bypassCache);

    // Check cache for GET requests (skip if bypassing cache)
    if (method === "GET" && useCache && !bypassCache) {
      const cacheKey = this.cacheManager.getCacheKey(fullUrl, data);
      const cached = this.cacheManager.getFromCache<T>(cacheKey);
      if (cached) return cached;
    }

    // Handle authentication
    if (!bypassAuth && this.tokenManager.getAuthToken()) {
      const tokenExpired = await this.tokenManager.checkTokenExpiry();
      if (tokenExpired) {
        const refreshed = await this.tokenManager.refreshAuthToken();
        if (!refreshed) {
          throw new AuthenticationError(
            "Session expired. Please log in again."
          );
        }
      }
    }

    // If offline, add to queue for non-critical GET requests
    if (!this.networkMonitor.isConnected() && method !== "GET") {
      const queuedRequest: QueuedRequest = {
        id: `${Date.now()}-${Math.random()}`,
        url: fullUrl, // Use full URL with params
        method,
        data,
        headers,
        timestamp: Date.now(),
        retryCount: 0,
        priority,
      };

      this.requestQueue.addToQueue(queuedRequest);
      throw new NetworkError("Request queued for when connection is restored");
    }

    if (!this.networkMonitor.isConnected()) {
      throw new NetworkError(API_ERROR_MESSAGES.NETWORK_ERROR);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.config.timeout
      );

      // Use standard headers without cache busting headers
      const requestHeaders = {
        "Content-Type": "application/json",
        ...headers,
        ...(!bypassAuth && this.tokenManager.getAuthToken()
          ? {
            Authorization: `Token ${this.tokenManager.getAuthToken()}`,
          }
          : {}),
      };

      const response = await fetch(`${this.config.baseURL}${fullUrl}`, {
        method,
        headers: requestHeaders,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await this.handleHttpError(response);
      }

      const result = await response.json();

      // Cache successful GET responses (skip if bypassing cache)
      if (method === "GET" && useCache && !bypassCache) {
        const cacheKey = this.cacheManager.getCacheKey(fullUrl, data);
        this.cacheManager.setCache(cacheKey, result);
      }

      return result;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new NetworkError(API_ERROR_MESSAGES.TIMEOUT_ERROR);
      }
      throw error;
    }
  }

  private async executeRequest(request: QueuedRequest): Promise<any> {
    const response = await fetch(`${this.config.baseURL}${request.url}`, {
      method: request.method,
      headers: {
        "Content-Type": "application/json",
        ...request.headers,
        ...(this.tokenManager.getAuthToken()
          ? {
            Authorization: `Token ${this.tokenManager.getAuthToken()}`,
          }
          : {}),
      },
      body: request.data ? JSON.stringify(request.data) : undefined,
    });

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText);
    }

    return response.json();
  }

  private async handleHttpError(response: Response): Promise<never> {
    let errorMessage = API_ERROR_MESSAGES.UNKNOWN_ERROR;
    let errorData: any = null;

    try {
      errorData = await response.json();

      // Handle the actual API error response format
      if (errorData.error_message) {
        errorMessage = errorData.error_message;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Failed to parse error response
    }

    switch (response.status) {
      case HTTP_STATUS.UNAUTHORIZED: {
        this.tokenManager.clearTokens();
        // notify app to logout and redirect with role context
        const currentRole = this.tokenManager.getCurrentRole();
        window.dispatchEvent(new CustomEvent("auth:logout", { detail: { role: currentRole } }));
        throw new AuthenticationError(
          errorMessage || API_ERROR_MESSAGES.AUTHENTICATION_ERROR
        );
      }
      case HTTP_STATUS.FORBIDDEN: {
        // treat forbidden as forced logout as well
        this.tokenManager.clearTokens();
        const forbiddenRole = this.tokenManager.getCurrentRole();
        window.dispatchEvent(new CustomEvent("auth:logout", { detail: { role: forbiddenRole } }));
        throw new ApiError(
          response.status,
          errorMessage || API_ERROR_MESSAGES.AUTHORIZATION_ERROR,
          errorData
        );
      }
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        throw new ApiError(
          response.status,
          errorMessage || API_ERROR_MESSAGES.SERVER_ERROR,
          errorData
        );
      default:
        throw new ApiError(response.status, errorMessage, errorData);
    }
  }

  // ==================== PUBLIC API METHODS ====================

  // Authentication
  public async authenticate(credentials: AuthRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>(
      API_ENDPOINTS.AUTH.AUTHENTICATE,
      {
        method: "POST",
        data: credentials,
        bypassAuth: true,
        priority: REQUEST_PRIORITY.AUTH,
      }
    );

    // Handle successful response
    if (response.token) {
      // Role is required for authentication
      const role = credentials.role;
      
      // Store tokens with role context
      this.tokenManager.storeTokens(response.token, response.refresh_token, role);

      if (response.user) {
        localStorage.setItem(
          CACHE_KEYS.USER_DATA,
          JSON.stringify(response.user)
        );
      }
    }

    console.log("Authentication response:", response);

    return response;
  }

  public logout(): void {
    this.tokenManager.clearTokens();
    this.cacheManager.clearCache();
    this.requestQueue.clearQueue();
  }

  public getCurrentRole(): string | null {
    return this.tokenManager.getCurrentRole();
  }

  public setActiveRole(role: "manager" | "verificator" | "operator"): void {
    this.tokenManager.setCurrentRole(role);
  }

  public logoutForRole(role: "manager" | "verificator" | "operator"): void {
    this.tokenManager.clearTokensForRole(role);
  }

  public isAuthenticated(): boolean {
    return this.tokenManager.isAuthenticated();
  }

  // Member Management
  public async getMembers(request: {
    params: { role: string };
  }): Promise<MemberResponse> {
    return this.request<MemberResponse>(
      `${API_ENDPOINTS.AUTH.PUBLIC_MEMBER_LIST}stch/`,
      {
        method: "GET",
        params: request.params,
        priority: REQUEST_PRIORITY.HIGH,
      }
    );
  }

  // Member Management
  public async manageMember(request: MemberRequest): Promise<MemberResponse> {
    let method: "GET" | "POST" | "PUT" | "DELETE" = "GET";

    switch (request.action) {
      case "list":
        method = "GET";
        // Add any filtering params for list
        break;
      case "create":
        method = "POST";
        break;
      case "update":
        method = "PUT";
        break;
      case "delete":
        method = "DELETE";
        break;
      default:
        throw new ApiError(
          HTTP_STATUS.BAD_REQUEST,
          "Invalid action for member management"
        );
    }

    return this.request<MemberResponse>(
      `${API_ENDPOINTS.AUTH.MEMBER_MANAGEMENT}${request.slug ?? "stch"}/`,
      {
        method,
        data: request.data,
        params: request.params,
        priority: REQUEST_PRIORITY.HIGH,
      }
    );
  }

  public async createShift(data: CreateShiftRequest): Promise<Shift> {
    return this.request<Shift>(API_ENDPOINTS.OPERATIONS.CREATE_SHIFT, {
      method: "POST",
      data,
      priority: REQUEST_PRIORITY.HIGH,
    });
  }

  // Enhanced shift detail method with cache bypass option
  public async getShiftDetail(
    shiftId: string,
    options?: { bypassCache?: boolean }
  ): Promise<Shift> {
    const response = await this.request<Shift>(
      API_ENDPOINTS.OPERATIONS.SHIFT_DETAIL(shiftId),
      {
        method: "GET",
        priority: REQUEST_PRIORITY.HIGH,
        useCache: !options?.bypassCache, // Don't use cache if bypassing
        bypassCache: options?.bypassCache || false,
      }
    );
    return response;
  }

  // Enhanced shifts list with cache bypass for refresh
  public async getShifts(
    filters?: {
      status?: string;
      page?: number;
      limit?: number;
    },
    options?: { bypassCache?: boolean }
  ): Promise<Shift[]> {
    const response = await this.request<Shift[]>(
      API_ENDPOINTS.OPERATIONS.SHIFTS,
      {
        method: "GET",
        params: filters,
        useCache: !options?.bypassCache,
        bypassCache: options?.bypassCache || false,
        priority: REQUEST_PRIORITY.NORMAL,
      }
    );
    return response || [];
  }

  public async updateShiftStatus(
    shiftId: string,
    new_status: ShiftStatusRequest
  ): Promise<void> {
    await this.request(API_ENDPOINTS.OPERATIONS.CHANGE_SHIFT_STATUS(shiftId), {
      method: "PUT",
      data: new_status,
      priority: REQUEST_PRIORITY.HIGH,
    });
  }

  // Scan History Management - Updated with query param support
  public async getScanHistory(
    filters?: {
      startDate?: string;
      endDate?: string;
      operatorName?: string;
      slotId?: number;
      sessionId?: string;
      page?: number;
      limit?: number;
    },
    options?: { bypassCache?: boolean }
  ): Promise<ScanHistoryResponse> {
    return this.request<ScanHistoryResponse>(
      API_ENDPOINTS.OPERATIONS.BARCODE_SCAN_HISTORY,
      {
        method: "GET",
        params: filters,
        useCache: true,
        bypassCache: options?.bypassCache || false,
        priority: REQUEST_PRIORITY.NORMAL,
      }
    );
  }

  public async createBulkScans(data: BulkScanRequest): Promise<void> {
    await this.request(API_ENDPOINTS.OPERATIONS.BULK_CREATE_SCANS, {
      method: "POST",
      data,
      priority: REQUEST_PRIORITY.HIGH,
    });
  }

  // Bitbox Block Management - Enhanced with status filtering
  public async recordBitboxBlock(data: BitboxBlockRequest): Promise<void> {
    await this.request(API_ENDPOINTS.STCH.RECORD_BITBOX_BLOCK, {
      method: "POST",
      data,
      priority: REQUEST_PRIORITY.HIGH,
    });
  }

  public async getBitboxBlocks(filters?: {
    startDate?: string;
    endDate?: string;
    operatorName?: string;
    status?: "pass" | "fail"; // NEW: Status filter for valid/invalid sets
    sessionId?: string;
    page?: number;
    limit?: number;
  }): Promise<BitboxBlockResponse> {
    return this.request<BitboxBlockResponse>(
      API_ENDPOINTS.STCH.LIST_BITBOX_BLOCKS,
      {
        method: "GET",
        params: filters,
        useCache: false,
        priority: REQUEST_PRIORITY.NORMAL,
      }
    );
  }

  // Bitbox Report Management - NEW
  public async generateBitboxReport(
    data: BitboxReportRequest
  ): Promise<BitboxReportResponse> {
    return this.request<BitboxReportResponse>(
      API_ENDPOINTS.STCH.BITBOX_REPORT,
      {
        method: "POST",
        data,
        priority: REQUEST_PRIORITY.HIGH,
      }
    );
  }

  // Utility Methods
  public getNetworkStatus(): boolean {
    return this.networkMonitor.isConnected();
  }

  public getQueueLength(): number {
    return this.requestQueue.getQueueLength();
  }

  public clearCache(): void {
    this.cacheManager.clearCache();
  }

  public updateConfig(newConfig: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  public destroy(): void {
    this.networkMonitor.destroy();
    this.requestQueue.destroy();
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;
